import React, { useEffect, useState } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Button, Form } from "antd";
import moment from "moment";
import { useAccountInfoContainerContext } from "../../containers/AccountInfoContainer/AccountInfoContainerContext";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import {
  toDisplayFormat,
  toHttpFormatStartOfDay,
  toYearMonthDayLineFormat,
} from "../../lib/utils/formatDate";
import ChangeVersionsTable from "../common/ChangeVersionsTable";
import { TableRecord } from "../../lib/interfaces/TableRecord";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";

const IndividualNameVersionsTable = () => {
  const { accountIndividual, accountIndividualLoading } = useSelector(
    (state: RootStateOrAny) => state.accounts
  );
  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const { individualVersions, individualVersionsLoading } = useSelector(
    (state: RootStateOrAny) => state.accountsTs
  );
  
  const [form] = Form.useForm();
  const [objectId, setObjectId] = useState(null);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const {
    getIndividualVersions,
    handleUpdateIndividual,
    handleCloseIndividual,
  } = useAccountInfoContainerContext();

  useEffect(() => {
    if (checkIsLoaded(accountIndividualLoading)) {
      setTableData([
        {
          key: accountIndividual.id,
          id: accountIndividual.id,
          lastName: accountIndividual.lastName,
          firstName: accountIndividual.firstName,
          middleName: accountIndividual.middleName,
          fromDate: accountIndividual.fromDate,
          toDate: accountIndividual.toDate,
          birthDate: accountIndividual?.birthDate
            ? moment(accountIndividual.birthDate, "YYYY-MM-DD").format("DD.MM.YYYY")
            : "",
        },
      ]);
    }
  }, [accountIndividualLoading]);

  useEffect(() => {
    const childData = individualVersions
      .map((version, i) => ({
        ...version,
        ...versionsTableUtils.getCommonChildTableProps(version, i, individualVersions.length),
        birthDate: version.birthDate
          ? moment(version.birthDate, "YYYY-MM-DD").format("DD.MM.YYYY")
          : "",
      }))
      .sort(versionsTableUtils.sortVersionsByDate);
    setChildTableData(childData);
  }, [individualVersions]);

  const showHistoryModal = (rec) => {
    setHistoryModalVisible(true);
    setObjectId(rec.id);
  };

  const handleHideModal = () => {
    setHistoryModalVisible(false);
    setObjectId(null);
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    lastName: "15%",
    firstName: "17%",
    middleName: "17%",
    birthDate: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
    },
    {
      title: "Дата рождения",
      dataIndex: "birthDate",
      key: "birthDate",
      width: columnsWidths.birthDate,
      hidden: false,
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render(text, record) {
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
      render(text, record) {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              showHistoryModal({ id: record.id });
            }}
            className="iconButton"
            type="text"
            htmlType="button"
            icon={<DotsIcon />}
          />
        );
      },
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Фамилия",
      dataIndex: "lastName",
      key: "lastName",
      width: columnsWidths.lastName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="lastName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          allowClear
        />
      ),
    },
    {
      title: "Имя",
      dataIndex: "firstName",
      key: "firstName",
      width: columnsWidths.firstName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="firstName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          allowClear
        />
      ),
    },
    {
      title: "Отчество",
      dataIndex: "middleName",
      key: "middleName",
      width: columnsWidths.middleName,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="middleName"
          record={record}
          setFieldsValue={form.setFieldsValue}
          allowClear
        />
      ),
    },
    {
      title: "Дата рождения",
      dataIndex: "birthDate",
      key: "birthDate",
      width: columnsWidths.birthDate,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Date
          dateName="birthDate"
          record={record}
          setFieldsValue={form.setFieldsValue}
          dateStringFormat="DD.MM.YYYY"
          allowClear
        />
      ),
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            {/* <ChangeVersionsTable.CloseButton
              hasAccess={isHasAccess}
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            /> */}
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const onFinish = (values) => {
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);

    if (changeableRecord.isClosing) {
      handleCloseIndividual(values.toDate);
    } else {
      const isForceUpdate = Boolean(changeableRecord.isEditing);
      const fromDate = isForceUpdate
        ? changeableRecord.fromDate
        : toHttpFormatStartOfDay(values.fromDate);
      const birthDateParse = values.birthDate ? toYearMonthDayLineFormat(values.birthDate) : null;
      handleUpdateIndividual({
        fromDate,
        data: {
          firstName: values.firstName,
          middleName: values.middleName,
          lastName: values.lastName,
          sex: accountIndividual.sex,
          birthDate: birthDateParse,
        },
        force: isForceUpdate,
      });
    }
  };

  return (
    <>
      <Form onFinish={onFinish} form={form}>
        <h4 style={{ marginTop: "45px" }}>ФИО</h4>
        <ToggleableVersionsTable
          tableName="individual-name-versions-table"
          columns={columns}
          setColumns={setColumns}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          tableData={tableData}
          childTableData={childTableData}
          isChildTableLoading={checkIsLoading(individualVersionsLoading)}
          tableClassName="individual-name-versions-table"
          isPageable={false}
          isToggleableColumns={false}
          onOpenVersionsCallback={() => {
            getIndividualVersions();
          }}
        />
      </Form>
      <HistoryAndPropertyModal
        objectTypeSysname="individual"
        objectId={objectId}
        handleHideModal={handleHideModal}
        visibleModal={historyModalVisible}
        hasAccessToEdit={hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE)}
      />
    </>
  );
};

export default IndividualNameVersionsTable;
